import { useMemo, useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxIcon,
  ComboboxItem,
  ComboboxList,
  ComboboxSearchInput,
  ComboboxTrigger,
  ComboboxValue,
} from '@/components/ui/combobox';
import { useDebounce } from '@/hooks/useDebounce';
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll';
import { assignedInstitutesInfiniteQueryOptions } from '@/features/userInstituteMapping/api/userInstituteMapping.api';

export interface PcmpInstituteOption {
  id: number;
  name: string;
  place: string | null;
}

interface PcmpInstitutePickerProps {
  /** The proposal's MO: only institutes assigned to them can take the submission. */
  userId: number;
  value: PcmpInstituteOption | null;
  onChange: (value: PcmpInstituteOption | null) => void;
  id?: string;
  'aria-invalid'?: boolean;
}

const PAGE_SIZE = 20;

/** Server-searched list of the MO's assigned institutes. */
export function PcmpInstitutePicker({
  userId,
  value,
  onChange,
  id,
  'aria-invalid': ariaInvalid,
}: PcmpInstitutePickerProps) {
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search.trim());

  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } = useInfiniteQuery(
    assignedInstitutesInfiniteQueryOptions({
      userId,
      limit: PAGE_SIZE,
      ...(debouncedSearch ? { name: debouncedSearch } : {}),
    }),
  );

  const options = useMemo<PcmpInstituteOption[]>(
    () =>
      data?.pages.flatMap((page) =>
        page.data.map((institute) => ({
          id: institute.id,
          name: institute.name,
          place:
            [institute.thana?.name, institute.district?.name].filter(Boolean).join(', ') || null,
        })),
      ) ?? [],
    [data],
  );

  const sentinelRef = useInfiniteScroll<HTMLDivElement>({
    onLoadMore: () => void fetchNextPage(),
    hasMore: hasNextPage,
    isLoadingMore: isFetchingNextPage,
  });

  return (
    <Combobox<PcmpInstituteOption | null>
      items={options}
      value={value}
      onValueChange={(next) => onChange(next)}
      inputValue={search}
      onInputValueChange={setSearch}
      filter={null}
      itemToStringLabel={(item) => item?.name ?? ''}
      isItemEqualToValue={(item, selected) => item?.id === selected?.id}
    >
      <ComboboxTrigger id={id} aria-invalid={ariaInvalid}>
        <ComboboxValue placeholder="Select an institute" />
        <ComboboxIcon />
      </ComboboxTrigger>
      <ComboboxContent>
        <ComboboxSearchInput placeholder="Search by name..." />
        <ComboboxList>
          {options.map((option) => (
            <ComboboxItem key={option.id} value={option}>
              <div className="flex min-w-0 flex-col">
                <span className="truncate">{option.name}</span>
                <span className="text-xs text-muted-foreground">
                  #{option.id}
                  {option.place ? ` · ${option.place}` : ''}
                </span>
              </div>
            </ComboboxItem>
          ))}
        </ComboboxList>
        {isLoading || isFetchingNextPage ? (
          <div className="flex justify-center py-2">
            <Loader2 className="size-4 animate-spin text-muted-foreground" />
          </div>
        ) : null}
        <div ref={sentinelRef} className="h-px" />
        {isLoading ? null : <ComboboxEmpty>No assigned institutes found.</ComboboxEmpty>}
      </ComboboxContent>
    </Combobox>
  );
}
